import React, { useEffect, useMemo, useCallback } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { IconPrivateAvatar } from '@/assets/icon/avatar';
import { IconRcEtc } from '@/assets/sidebar';
import { Button, UserAvatar } from '@/components';
import {
  useCurrentAddressInfo,
  useUserDetail,
  useSwitch,
  usePrivate,
  useUpdateCurrentAddressInfo,
  useLoginedAddresses,
  useConnectedAddress,
} from '@/hooks';
import { login, shorten, shortenAddress } from '@/utils';

import { useAccountsNotification } from '../../hooks/account';
import { AccountSwitcherModal } from './AccountSwitcherModal';
import style from './index.module.scss';

type Props = {
  className?: string;
  mini?: boolean;
};

export const AccountSwitcher = (props: Props) => {
  const { className, mini } = props;
  const { t } = useTranslation();

  const history = useHistory();
  const { on, turnOff, turnOn } = useSwitch();

  const { address } = useCurrentAddressInfo();
  const loginedList = useLoginedAddresses();
  const connectedAddress = useConnectedAddress();
  const [isPrivate] = usePrivate();
  const { data: userInfo } = useUserDetail(address);

  const update = useUpdateCurrentAddressInfo();

  const { hasNoti } = useAccountsNotification();

  useEffect(() => {
    if (userInfo) {
      update(pre => {
        const next = { ...pre };
        if (next.userInfo && pre.userInfo?.id === userInfo.id) {
          next.userInfo = userInfo;
        }
        if (next.detail && pre.detail?.id === userInfo.id) {
          next.detail = undefined;
          next.userInfo = userInfo;
        }

        return next;
      });
    }
  }, [update, userInfo]);

  const currentAccount = useMemo(() => {
    if (!address) return '-';
    const account = userInfo?.desc?.user.web3_id || shortenAddress(address);

    return isPrivate ? '***********' : account;
  }, [isPrivate, address, userInfo]);

  const handleLoginClick = useCallback(() => {
    login();
  }, []);

  const handleConnectedAddrClick = useCallback(() => {
    history.push(`/profile/${connectedAddress}`);
  }, [history, connectedAddress]);

  return (
    <div className={cx(style.container, mini && style.isMini, className)}>
      {connectedAddress && loginedList?.every(x => x.id !== connectedAddress) ? (
        <div className={style.connectedUser} onClick={handleConnectedAddrClick}>
          {mini ? null : <div className={style.connectedUserTitle}>Current address of wallet</div>}
          <div className={style.connectedUserDesc}>
            <UserAvatar size={24} />
            {mini ? null : (
              <div className={style.connectedAddress}>{shorten(connectedAddress, 6, 4)}</div>
            )}
          </div>
        </div>
      ) : null}
      {address ? (
        <Button type="gray" className={style.mainBtn} onClick={turnOn}>
          {isPrivate ? (
            <img src={IconPrivateAvatar} alt="avatar" width={28} height={28} />
          ) : (
            <UserAvatar data={userInfo?.desc} size={28} />
          )}

          {mini ? null : (
            <div className={cx(style.addrInfo, address && style.isVerified)}>{currentAccount}</div>
          )}
          {!isPrivate && hasNoti ? <div className={style.notiDot} /> : null}
          <IconRcEtc className={style.iconEtc} />
        </Button>
      ) : (
        <Button type="primary" className={style.mainBtnLogin} onClick={handleLoginClick}>
          {t('Welcome.oivb3w')}
        </Button>
      )}
      <AccountSwitcherModal onClose={turnOff} on={on} />
    </div>
  );
};
